import { makeStyles } from '@material-ui/styles'
import React from 'react'
import { mfaDoc } from 'app/docLinks'
import ExternalLink from 'core/components/ExternalLink'
import SimpleLink from 'core/components/SimpleLink'
import Checkbox from 'core/elements/input/Checkbox'
import Input from 'core/elements/input/Input'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip'
import Theme from 'core/themes/model'
import generateTestId from 'utils/test-helpers'

const useStyles = makeStyles((theme: Theme) => ({
  passwordInputField: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '& .input[type=password]::-ms-reveal, & .input[type=password]::-ms-clear': {
      display: 'none',
    },
  },
  mfa: {
    lineHeight: '16px',
    display: 'flex',
    alignItems: 'start',
    gap: 4,
    '& p': {
      lineHeight: '16px',
    },
  },
  mfaInfo: {
    '& > span': {
      lineHeight: '16px',
    },
  },
  forgotPwd: {
    alignSelf: 'end',
    lineHeight: '18px',
    marginTop: 8,
  },
}))

interface CredentialLoginFormProps {
  username: string
  password: string
  domain: string
  MFAcheckbox: boolean
  mfa: string
  showPassword: boolean
  loginFailed: boolean
  shouldRedirect?: boolean
  forgotPasswordUrl?: string
  updateValue: (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => void
  setShowPassword: (show: boolean) => void
  handleChangeBox: (name: string) => (checked: boolean) => void
  handleForgotPassword: () => (e: React.MouseEvent) => void
}

const CredentialLoginForm: React.FC<CredentialLoginFormProps> = ({
  username,
  password,
  domain,
  MFAcheckbox,
  mfa,
  showPassword,
  loginFailed,
  shouldRedirect,
  forgotPasswordUrl,
  updateValue,
  setShowPassword,
  handleChangeBox,
  handleForgotPassword,
}) => {
  const classes = useStyles({})

  return (
    <>
      <Input
        required
        variant="dark"
        id="email"
        label="Email"
        placeholder="Email"
        value={username}
        onChange={updateValue('username')}
      />
      <div className={classes.passwordInputField}>
        <Input
          required
          iconProps={{ placement: 'end', onClick: () => setShowPassword(!showPassword) }}
          icon={showPassword ? 'eye' : 'eye-slash'}
          variant="dark"
          id="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={updateValue('password')}
        />
      </div>
      <div className={classes.passwordInputField}>
        <Input
          variant="dark"
          id="domain"
          label="Domain"
          type="text"
          value={domain}
          onChange={updateValue('domain')}
        />
        <Text className={classes.forgotPwd}>
          <SimpleLink
            src={shouldRedirect ? forgotPasswordUrl : undefined}
            onClick={!shouldRedirect ? handleForgotPassword() : undefined}
            textVariant="caption2"
          >
            Forgot password?
          </SimpleLink>
        </Text>
      </div>
      {MFAcheckbox && (
        <Input
          required={MFAcheckbox}
          variant="dark"
          id="mfa"
          label="MFA Code"
          placeholder="MFA Code"
          value={mfa}
          onChange={updateValue('mfa')}
        />
      )}
      <Checkbox
        checked={MFAcheckbox}
        onChange={handleChangeBox('MFAcheckbox')}
        label={
          <Text component="div" variant="body2" className={classes.mfa}>
            I have a
            <Tooltip message="Multi-Factor Authentication">
              <Text variant="body2">MFA</Text>
            </Tooltip>
            token.
            <ExternalLink className={classes.mfaInfo} textVariant="body2" url={mfaDoc}>
              More info
            </ExternalLink>
          </Text>
        }
      />
    </>
  )
}

export default CredentialLoginForm
